// src/components/exam-player/renderers/OrderingRenderer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

function shuffleArray(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export default function OrderingRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const body = item?.body || {};
  const items = Array.isArray(body.items) ? body.items : [];
  const layout = body.layout || "top-bottom";

  const isVertical =
    layout === "top-bottom" || layout === "bottom-top";

  // آیتم‌ها با ایندکس ثابت
  const baseItems = useMemo(
    () =>
      items.map((it, idx) => ({
        id: String(idx),
        label:
          it?.label?.fa ||
          it?.label?.en ||
          t("ordering.item_default", { n: idx + 1 }),
      })),
    [items, t]
  );

  // ✅ ترتیب پیش‌فرض (برای زمانی که initialAnswers نداریم) → شافل
  const defaultOrder = useMemo(() => {
    const ids = baseItems.map((it) => it.id);
    return shuffleArray(ids);
  }, [baseItems]);

  // مقدار اولیه از initialAnswers (اگر بود) وگرنه شافل
  const initialOrder = useMemo(() => {
    if (initialAnswers) {
      if (
        typeof initialAnswers === "object" &&
        Array.isArray(initialAnswers.order)
      ) {
        return initialAnswers.order.map((x) => String(x));
      }
      if (Array.isArray(initialAnswers)) {
        return initialAnswers.map((x) => String(x));
      }
    }
    // 👇 اگر initialAnswers نبود → از defaultOrder استفاده کن
    return defaultOrder;
  }, [initialAnswers, defaultOrder]);

  const [order, setOrder] = useState(initialOrder);

  useEffect(() => {
    setOrder(initialOrder);
  }, [initialOrder]);

  const handleMove = (idx, dir) => {
    setOrder((prev) => {
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      const [it] = next.splice(idx, 1);
      next.splice(j, 0, it);
      onAnswerChange?.({ order: next }); // فرم استاندارد برای بک‌اند
      return next;
    });
  };

  if (!baseItems.length) {
    return (
      <div className="text-sm text-red-600">
        {t("preview.kind_not_supported", { kind: "ordering" })}
      </div>
    );
  }

  const stemHtml =
    item?.stem?.fa || item?.stem?.en || t("preview.no_stem", "بدون متن");

  const containerClass = isVertical
    ? "flex flex-col gap-2 mt-2"
    : "flex flex-row flex-wrap gap-2 mt-2";

  return (
    <div dir={isRtl ? "rtl" : "ltr"}>
      {/* Stem */}
      <div className="prose prose-sm max-w-none mb-4">
        <div dangerouslySetInnerHTML={{ __html: stemHtml }} />
        {/* {typeof questionNumber === "number" && (
          <div className="text-xs text-gray-500 mt-1">
            {t("preview.question_number", { n: questionNumber })}
          </div>
        )} */}
      </div>

      {/* لیست مرتب‌سازی */}
      <div className={containerClass}>
        {order.map((id, idx) => {
          const itemDef = baseItems.find((it) => it.id === id);
          const label = itemDef?.label || id;
          return (
            <div
              key={id}
              className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-white text-sm"
            >
              <span className="text-xs text-gray-500 min-w-[24px] text-center">
                {idx + 1}
              </span>
              <span className="flex-1">{label}</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="p-1 rounded-full text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                  onClick={() => handleMove(idx, -1)}
                  disabled={idx === 0}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="p-1 rounded-full text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                  onClick={() => handleMove(idx, +1)}
                  disabled={idx === order.length - 1}
                >
                  ↓
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
